import { memo } from 'react';
import styles from './FilterGroups.module.scss';
import { FilterGroupsProps, FilterCategory } from '@interfaces/filter.types';
import FilterGroup from '@components/atoms/FilterGroup';
import BudgetFilter from '@components/atoms/BudgetFilter';
import { filterHelpers } from '@helpers/filterLogic';
import { useFilter } from '@context/FilterContext';
import { createUnifiedFilterConfig } from '@helpers/filterConfigHelpers';
import Text from '@components/atoms/Text';
import CarLoader from '@components/atoms/Loader';

const FilterGroups = memo(({
  filters,
  openSections,
  onFilterChange,
  onBudgetChange,
  onToggleSection
}: FilterGroupsProps) => {
  const { filterOptions, isLoading } = useFilter();
  
  if (isLoading || !filterOptions) {
    return (
      <CarLoader
        text="Loading filter..."
        theme="light"
        variant="inline"
        size="small"
      />
    );
  }

  const config = createUnifiedFilterConfig(filterOptions);

  const renderFilterGroups = () => {
    const filterGroups: React.ReactNode[] = [];
    
    config.categories.forEach((filterConfig) => {
      if (!filterConfig) return;
      
      if (filterConfig.type === 'range' && filterConfig.key === 'budget') {
        filterGroups.push(
          <BudgetFilter
            key={filterConfig.key}
            title={filterConfig.title}
            minValue={filters.budget?.min || filterConfig.config?.min || 0}
            maxValue={filters.budget?.max || filterConfig.config?.max || 0}
            onChange={onBudgetChange}
            step={filterConfig.config?.step || 100000}
            className=""
            filterOptions={filterOptions}
          />
        );
        return;
      }
      
      if (filterConfig.options && filterConfig.options.length > 0) {
        const filterValue = filters[filterConfig.key as keyof typeof filters];
        const selectedValues = Array.isArray(filterValue) ? filterValue : [];
        
        filterGroups.push(
          <FilterGroup
            key={filterConfig.key}
            title={filterConfig.title}
            options={JSON.parse(JSON.stringify(filterConfig.options))}
            selectedValues={selectedValues}
            onSelect={(values) => onFilterChange(filterConfig.key as FilterCategory, values)}
            isOpen={openSections[filterConfig.key]}
            onToggle={() => onToggleSection(filterConfig.key)}
            allowMultiSelect={!filterHelpers.isSingleSelectCategory(filterConfig.key as FilterCategory)}
            category={filterConfig.key as FilterCategory}
            filterOptions={filterOptions}
          />
        );
      }
    });
    
    return filterGroups;
  };

  const filterGroups = renderFilterGroups();

  return (
    <div className={styles.container}>
      {filterGroups.length > 0 ? (
        filterGroups
      ) : (
        <div className={styles.noFiltersAvailable}>
          <Text className={styles.noFiltersText}>
            No filters available for this selection.
          </Text>
        </div>
      )}
    </div>
  );
});

FilterGroups.displayName = 'FilterGroups';

export default FilterGroups; 
